import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Send, CheckCircle, FileText, Users, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuotePage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service_type: '',
    quantity: '',
    deadline: '',
    details: '',
  });

  const services = [
    'Business Cards',
    'Flyers',
    'Brochures',
    'Posters',
    'Banners',
    'Digital Printing',
    'Offset Printing',
    'Letterheads',
    'Envelopes',
    'Postcards',
    'Booklets',
    'Custom Print Job',
    'Other',
  ];

  const quantities = [
    '50-100',
    '100-250',
    '250-500',
    '500-1,000',
    '1,000-2,500',
    '2,500-5,000',
    '5,000-10,000',
    '10,000+',
  ];

  const deadlines = [
    'Same Day (Rush)',
    'Next Day',
    '2-3 Business Days',
    '1 Week',
    '2 Weeks',
    'Flexible',
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/quote`, formData);
      if (response.data.status === 'success') {
        setSubmitted(true);
        setQuoteNumber(response.data.quote_number);
        toast.success('Quote request submitted successfully!');
      }
    } catch (error) {
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="quote-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-white paper-texture" data-testid="quote-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-medium tracking-widest uppercase text-[#3EACDB]">Request a Quote</span>
            <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tighter uppercase mt-4">
              Get Your <span className="text-[#3EACDB]">Free</span> Quote
            </h1>
            <p className="mt-6 text-lg text-zinc-600">
              Fill out the form below and we'll get back to you with a detailed quote within 24 hours. Need it faster? Call us directly.
            </p>
            <a 
              href="tel:+12129711334" 
              className="inline-flex items-center mt-6 text-[#3EACDB] font-semibold hover:underline"
              data-testid="quote-call-link"
            >
              <Phone className="w-5 h-5 mr-2" />
              (212) 971-1334 - Call for immediate assistance
            </a>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-16 md:py-24 bg-zinc-50" data-testid="quote-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-zinc-200 p-8 md:p-10">
                {submitted ? (
                  <div className="text-center py-12" data-testid="quote-success">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">Quote Request Submitted!</h2>
                    <p className="text-zinc-600 mb-4">Your quote number is:</p>
                    <p className="text-2xl font-mono font-bold text-[#3EACDB] bg-zinc-100 inline-block px-6 py-3 rounded-sm">
                      {quoteNumber}
                    </p>
                    <p className="text-zinc-600 mt-6">
                      We'll review your request and send you a detailed quote within 24 hours.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                      <Link to="/" data-testid="quote-home-link">
                        <Button variant="outline" className="h-12 px-6 text-sm font-bold uppercase tracking-wider rounded-sm">
                          Back to Home
                        </Button>
                      </Link>
                      <a href="tel:+12129711334">
                        <Button className="h-12 px-6 text-sm font-bold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm">
                          <Phone className="mr-2 w-4 h-4" />
                          Call Us
                        </Button>
                      </a>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="font-heading text-2xl font-bold mb-2">Project Details</h2>
                    <p className="text-zinc-600 mb-8">Tell us about your printing project and we'll provide a custom quote.</p>

                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                      {/* Contact Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="name" className="text-sm font-medium">Full Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            className="mt-2 h-12 rounded-sm border-zinc-300 focus:ring-[#3EACDB] focus:border-[#3EACDB]"
                            placeholder="John Smith"
                            data-testid="quote-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            className="mt-2 h-12 rounded-sm border-zinc-300 focus:ring-[#3EACDB] focus:border-[#3EACDB]"
                            placeholder="john@company.com"
                            data-testid="quote-email-input"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="phone" className="text-sm font-medium">Phone Number</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            className="mt-2 h-12 rounded-sm border-zinc-300 focus:ring-[#3EACDB] focus:border-[#3EACDB]"
                            placeholder="(555) 123-4567"
                            data-testid="quote-phone-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="company" className="text-sm font-medium">Company Name</Label>
                          <Input
                            id="company"
                            name="company"
                            type="text"
                            value={formData.company}
                            onChange={handleChange}
                            className="mt-2 h-12 rounded-sm border-zinc-300 focus:ring-[#3EACDB] focus:border-[#3EACDB]"
                            placeholder="Acme Inc."
                            data-testid="quote-company-input"
                          />
                        </div>
                      </div>

                      {/* Project Details */}
                      <div className="border-t border-zinc-200 pt-6 mt-6">
                        <h3 className="font-heading text-lg font-semibold mb-4">Project Information</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div>
                            <Label className="text-sm font-medium">Service Type *</Label>
                            <Select 
                              value={formData.service_type} 
                              onValueChange={(value) => handleSelectChange('service_type', value)}
                              required
                            >
                              <SelectTrigger className="mt-2 h-12 rounded-sm" data-testid="quote-service-select">
                                <SelectValue placeholder="Select service" />
                              </SelectTrigger>
                              <SelectContent>
                                {services.map((service) => (
                                  <SelectItem key={service} value={service}>
                                    {service}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-sm font-medium">Quantity</Label>
                            <Select 
                              value={formData.quantity} 
                              onValueChange={(value) => handleSelectChange('quantity', value)}
                            >
                              <SelectTrigger className="mt-2 h-12 rounded-sm" data-testid="quote-quantity-select">
                                <SelectValue placeholder="Select quantity" />
                              </SelectTrigger>
                              <SelectContent>
                                {quantities.map((qty) => (
                                  <SelectItem key={qty} value={qty}>
                                    {qty}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-sm font-medium">Deadline</Label>
                            <Select 
                              value={formData.deadline} 
                              onValueChange={(value) => handleSelectChange('deadline', value)}
                            >
                              <SelectTrigger className="mt-2 h-12 rounded-sm" data-testid="quote-deadline-select">
                                <SelectValue placeholder="Select deadline" />
                              </SelectTrigger>
                              <SelectContent>
                                {deadlines.map((deadline) => (
                                  <SelectItem key={deadline} value={deadline}>
                                    {deadline}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="details" className="text-sm font-medium">Project Details *</Label>
                        <Textarea
                          id="details"
                          name="details"
                          required
                          rows={6}
                          value={formData.details}
                          onChange={handleChange}
                          className="mt-2 rounded-sm border-zinc-300 focus:ring-[#3EACDB] focus:border-[#3EACDB] resize-none"
                          placeholder="Please describe your project in detail. Include information about paper stock, finishes, colors, sizing, and any special requirements..."
                          data-testid="quote-details-input"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={loading || !formData.service_type}
                        className="w-full h-14 text-base font-bold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm"
                        data-testid="quote-submit-btn"
                      >
                        {loading ? (
                          'Submitting...'
                        ) : (
                          <>
                            Submit Quote Request
                            <Send className="ml-2 w-5 h-5" />
                          </>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Quick Info */}
                <div className="bg-white border border-zinc-200 p-6">
                  <h3 className="font-heading text-lg font-bold mb-4">What to Expect</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-[#3EACDB]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-[#3EACDB]" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Detailed Quote</p>
                        <p className="text-xs text-zinc-500">Itemized pricing breakdown</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-[#3EACDB]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-4 h-4 text-[#3EACDB]" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">24-Hour Response</p>
                        <p className="text-xs text-zinc-500">Business day turnaround</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-[#3EACDB]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <Users className="w-4 h-4 text-[#3EACDB]" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Expert Consultation</p>
                        <p className="text-xs text-zinc-500">Free design advice included</p>
                      </div>
                    </li>
                  </ul>
                </div>

                {/* Contact Card */}
                <div className="bg-zinc-900 text-white p-6">
                  <h3 className="font-heading text-lg font-bold mb-4">Need It Faster?</h3>
                  <p className="text-zinc-400 text-sm mb-4">
                    Call us directly for immediate assistance with your printing project.
                  </p>
                  <a href="tel:+12129711334" data-testid="sidebar-call-btn">
                    <Button className="w-full h-12 text-sm font-bold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm">
                      <Phone className="mr-2 w-4 h-4" />
                      (212) 971-1334
                    </Button>
                  </a>
                  <div className="mt-4 text-xs text-zinc-500 text-center">
                    Mon-Fri: 9:00 AM - 5:30 PM
                  </div>
                </div>

                {/* Visit Us */}
                <div className="bg-white border border-zinc-200 p-6">
                  <h3 className="font-heading text-lg font-bold mb-4">Visit Our Shop</h3>
                  <p className="text-sm text-zinc-600">
                    247 W 35th St, Suite #203<br />
                    New York, NY 10001
                  </p>
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center mt-4 text-[#3EACDB] text-sm font-semibold hover:underline"
                    data-testid="sidebar-directions-link"
                  >
                    Get Directions
                    <ArrowRight className="ml-1 w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuotePage;
